'use client';
import {getStrapiData} from '@/app/lib/api';
import {cn} from '@/lib/utils';
import {stringMap} from '@/types/utils';
import parse from 'html-react-parser';
import moment, {Moment} from 'moment';
import {useLocale, useTranslations} from 'next-intl';
import {useSearchParams} from 'next/navigation';
import {useEffect, useRef, useState} from 'react';
import {DatePicker} from '../ui/date-picker';
import Human from './Human';

type ChangeEvent = React.ChangeEvent<
  HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
>;

export default function ReservationFormBuilder(prop: {
  page_slug: string;
  form_slug: string;
  meta: any;
}) {
  const searchParams = useSearchParams();
  const t = useTranslations('reservationFormBuilder');
  const locale = useLocale();
  const {meta, page_slug, form_slug} = prop;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formStatus, setFormStatus] = useState(201);
  const [formData, setFormData] = useState<any>({});
  const [errorMsg, setErrorMsg] = useState(t('submitFailDescription'));
  const [fieldErrorMap, setFieldErrorMap] = useState<any>({});
  const [isLoadingFirst, setIsLoadingFirst] = useState(true);
  const [reservationTimeslots, setReservationTimeslots] = useState<any>({});
  const [selectedDates, setSelectedDates] = useState<stringMap>({});
  const [reservationRules, setReservationRules] = useState<any>({});
  const [sysCode, setSysCode] = useState('');

  let reservation_slugs: string[] = [];
  const dateFormat = 'YYYY/MM/DD';
  const dateFormatFull = 'YYYY-MM-DD HH:mm';
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const hash = useRef<string>(meta.hash);
  const errorMsgMap: {[key: string]: string} = {
    NOT_VALID_RESERVATION_EMAIL: t('error.NOT_VALID_RESERVATION_EMAIL'),
    9900002: t('error.RATE_LIMIT_EXCEEDED'),
    9901002: t('error.INVALID_TOKEN'),
    9901003: t('error.TOKEN_EXPIRED')
  };

  const updateFormData = (e: ChangeEvent, fieldKey: string) => {
    setFormData((prev: any) => ({...prev, [fieldKey]: e.target.value}));
  };

  const onInput = (e: any, fieldKey: string) => {
    setFieldErrorMap((prev: any) => ({...prev, [fieldKey]: ''}));
    e?.target?.setCustomValidity('');
  };
  const onInvalid = (e: any, field: string, fieldKey: string) => {
    const err = t('plsEnterField', {field});
    e?.target?.setCustomValidity(err);
    setFieldErrorMap((prev: any) => ({...prev, [fieldKey]: err}));
  };
  const onInvalidCheck = (e: any) => {
    e?.target?.setCustomValidity(t('missingCheckField'));
  };

  const formSectionTitle = (fieldTitle: string, fieldSettings: any) => (
    <div
      className={cn(
        'font-[700] abhaya-libre text-[28px]',
        'leading-[40px] mb-[60px] md:col-span-4 col-span-2',
        fieldSettings?.t_class
      )}
    >
      {fieldTitle}
      {fieldSettings?.required && <span className="text-red-500">*</span>}
    </div>
  );

  const formTitle = (
    fieldKey: string,
    fieldTitle: string,
    fieldSettings: any
  ) => (
    <label
      className={cn('font-[500] afacad pb-2', fieldSettings?.t_class)}
      htmlFor={fieldKey}
    >
      {fieldTitle}
      {fieldSettings?.required && <span className="text-red-500">*</span>}
    </label>
  );

  const formCheckBox = (
    fieldKey: string,
    fieldTitle: string,
    fieldSettings: any
  ) => (
    <div className="flex items-baseline justify-start gap-2">
      <div className="flex ">
        <input
          name={fieldKey}
          id={fieldKey}
          type="checkbox"
          className={cn(
            'border border-gray-300 rounded-sm w-full text-[20px] p-1 w-4 h-4',
            fieldSettings?.class
          )}
          placeholder={fieldSettings?.placeholder}
          value={formData[fieldKey] ? 'true' : 'false'}
          onChange={(e) => updateFormData(e, fieldKey)}
          required={fieldSettings?.required}
          maxLength={fieldSettings?.maxlength ?? 5000}
          onInput={(e: any) => {
            onInput(e, fieldKey);
          }}
          onInvalid={onInvalidCheck}
        />
      </div>
      <label
        className={cn('text-[20px]', fieldSettings?.t_class)}
        htmlFor={fieldKey}
      >
        {fieldSettings?.required ? (
          <div className="text-red-500  text-base w-1">*</div>
        ) : (
          <div className="text-base w-1">&nbsp;</div>
        )}
      </label>
      <label
        className={cn('text-[20px]', fieldSettings?.t_class)}
        htmlFor={fieldKey}
      >
        {parse(fieldTitle)}
      </label>
    </div>
  );

  const formOption = (options: string) => {
    return options.split(';').map((option, index) => {
      const match = option.match(/^([^:]+):(.*)$/);
      const optionValue = match?.[1] ?? option;
      const optionLabel = match?.[2] ?? optionValue;
      return (
        <option key={'option_' + index} value={optionValue}>
          {optionLabel ?? optionValue}
        </option>
      );
    });
  };

  const submitForm = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);
    // const data = Object.fromEntries(formData);
    setIsSubmitting(true);
    setIsSubmitted(false);
    setFormStatus(500);

    formData.append('page_slug', page_slug);
    formData.append('form_slug', form_slug);
    formData.append('hash', hash.current);

    if (meta.data) {
      formData.append('data', JSON.stringify(meta.data));
    }

    try {
      const response = await fetch(meta.url, {
        method: meta.method,
        body: formData
      });
      setIsSubmitting(false);

      if (response.ok) {
        const res = await response.json();
        const resStatus = res.status;
        setFormStatus(resStatus);
        setErrorMsg(
          errorMsgMap[res.message] ??
            errorMsgMap[res.sys_code] ??
            t('submitFailDescription')
        );
        setSysCode(res.sys_code?.toString());
        if (resStatus === 200) {
          setFormData({});
          setFieldErrorMap({});
        }
      }
    } finally {
      refreshTimeslots();
      setIsSubmitted(true);
    }
  };

  const checkReservation = (date: Date, slug: string) => {
    if (reservationTimeslots[slug]) {
      const dateStr = moment(date).format(dateFormat);
      if (reservationTimeslots[slug][dateStr]) {
        return false;
      }
    }
    return true;
  };

  const [availableTimeslots, setAvailableTimeslots] = useState<any>([]);
  const getReservation = async (slug: string) => {
    (
      await getStrapiData('en', 'reservations', {
        filters: {slug},
        populate: 'schedule'
      })
    )?.data?.map(async (reservation: any) => {
      const excludeDates = (reservation.exclude_dates ?? '').split(',');
      const eventEstMins = reservation.event_estimated_minutes;
      const participantQuota = reservation.participant_quota;
      const bookingLeadTime = moment().add(
        reservation.booking_lead_minutes,
        'minutes'
      );

      // sum no_of_participants group by start_time
      const confirmedReservations = (
        (
          await getStrapiData('en', 'reservation-submits', {
            filters: {
              reservation: {documentId: reservation.documentId},
              reservation_status: {$in: ['confirmed', 'pending']},
              start_time: {$gte: new Date()}
            },
            pagination: {pageSize: 9999}
          })
        )?.data ?? []
      ).reduce((acc: any, curr: any) => {
        const key = moment(curr.start_time).format(dateFormatFull);
        if (!acc[key]) {
          acc[key] = 0;
        }
        acc[key] += curr.no_of_participants;
        return acc;
      }, {});

      let currentSchedule = reservation.schedule
        ?.filter((s: any) => new Date(s.activate_at) <= today)
        ?.reduce((a: {activate_at: string}, b: {activate_at: string}) => {
          return new Date(a.activate_at) > new Date(b.activate_at) ? a : b;
        }, {}) ?? {activate_at: new Date()};

      const schedules = [
        currentSchedule,
        ...reservation.schedule.filter(
          (s: any) => new Date(s.activate_at) >= today
        )
      ];

      const handleTimeslot = (startTimes: string, currentDate: Moment) => {
        return startTimes
          ?.split(',')
          .filter((value, index, self) => self.indexOf(value) === index)
          .map((startTime) => {
            if (startTime && startTime.length > 0) {
              const startMoment = moment(
                currentDate.format(dateFormat) + ' ' + startTime
              );

              if (participantQuota > 0) {
                const confirmedQuota =
                  confirmedReservations[startMoment.format(dateFormatFull)] ??
                  0;
                if (confirmedQuota > participantQuota) {
                  return null;
                }
              }
              const endTime = moment(startMoment)
                .add(eventEstMins, 'minutes')
                .format('HH:mm');

              if (startMoment.isAfter(bookingLeadTime)) {
                return {
                  value: startTime,
                  label: startTime + ' - ' + endTime
                };
              }
            }
            return null;
          })
          .filter((t) => t);
      };

      let currentDate = new Date();
      currentDate.setHours(0, 0, 0, 0);
      let reservationTime: any = {};
      for (let i = 0; i < reservation.max_booking_advance_days; i++) {
        currentSchedule =
          schedules.find(
            (s: any) =>
              new Date(s.activate_at).toLocaleDateString() ===
              currentDate.toLocaleDateString()
          ) ?? currentSchedule;
        const currentDateStr = moment(currentDate).format(dateFormat);
        if (!excludeDates.includes(currentDateStr)) {
          const weekday = moment(currentDate).format('ddd').toLowerCase();
          if (currentSchedule[weekday]) {
            const timeslots = handleTimeslot(
              currentSchedule[weekday],
              moment(currentDate)
            );

            if (timeslots && timeslots.length > 0) {
              timeslots.map((timeslot: any) => {
                setAvailableTimeslots((prev: any) => [
                  ...prev,
                  {slug, date: currentDateStr, timeslot}
                ]);
              });
              reservationTime[currentDateStr] = timeslots;
            }
          }
        }
        currentDate.setDate(currentDate.getDate() + 1);
      }
      setReservationTimeslots((prev: any) => ({
        ...prev,
        [reservation.slug]: reservationTime
      }));
      setReservationRules((prev: any) => ({
        ...prev,
        [reservation.slug]: reservation.invite_rules
      }));
    });
  };

  const getReservationTimeslot = (slug: string) => {
    return reservationTimeslots[slug]?.[selectedDates[slug]] ?? [];
  };

  const validateEmail = (e: any, fieldKey: string) => {
    const fieldValue = formData[fieldKey];
    if (fieldValue) {
      const fieldMeta = meta?.fields.find((f: any) =>
        f[0].startsWith(fieldKey + ':')
      );
      const fieldSettings = fieldMeta?.[1];
      if (fieldSettings?.reservation_data) {
        const reservation_slug = fieldSettings?.reservation_data.split(':')[0];
        if (
          reservationRules[reservation_slug] &&
          reservationRules[reservation_slug].length > 0
        ) {
          let isValidEmail = false;
          for (const invite_rule of reservationRules[reservation_slug]) {
            const ruleValue = invite_rule.value.toLowerCase();
            ruleValue.split('\n').map((ruleLine: string) => {
              if (ruleLine.trim().length > 0) {
                if (invite_rule.type === 'email') {
                  if (ruleLine.trim() === fieldValue) {
                    isValidEmail = true;
                  }
                }
                if (invite_rule.type === 'domain') {
                  if (fieldValue.endsWith('@' + ruleLine.trim())) {
                    isValidEmail = true;
                  }
                }
              }
            });
          }
          if (!isValidEmail) {
            e?.target?.setCustomValidity(
              errorMsgMap.NOT_VALID_RESERVATION_EMAIL
            );
            setFieldErrorMap((prev: any) => ({
              ...prev,
              [fieldKey]: errorMsgMap.NOT_VALID_RESERVATION_EMAIL
            }));
          } else {
            e?.target?.setCustomValidity('');
            setFieldErrorMap((prev: any) => ({
              ...prev,
              [fieldKey]: ''
            }));
          }
        }
      }
    }
  };

  const refreshTimeslots = () => {
    reservation_slugs = [];
    setIsLoadingFirst(true);
    setTimeout(() => {
      meta?.fields.map((field: any, index: number) => {
        if (field?.[1]?.reservation_date) {
          const reservation_slug = field[1].reservation_date;
          if (!reservation_slugs.includes(reservation_slug)) {
            getReservation(reservation_slug);
            reservation_slugs.push(reservation_slug);
          }
        }
      });
    }, 1000);
  };

  useEffect(() => {
    if (searchParams.get('email')) {
      setFormData((prev: any) => ({
        ...prev,
        ['email']: searchParams.get('email')
      }));
    }
    refreshTimeslots();
  }, []);

  useEffect(() => {
    meta?.fields.map((field: any) => {
      if (field?.[1]?.reservation_date) {
        if (isLoadingFirst) {
          const fieldKey = field[0].split(':')[1];
          const reservation_slug = field[1].reservation_date;
          const availableDates = Object.keys(
            reservationTimeslots[reservation_slug] ?? []
          );
          if (availableDates.length > 0) {
            const selectDate = availableDates[0];
            setSelectedDates((prev: any) => ({
              ...prev,
              [reservation_slug]: selectDate
            }));
            setFormData((prev: any) => ({
              ...prev,
              [fieldKey]: moment(selectDate).toDate()
            }));
            setIsLoadingFirst(false);
          }
        }
      }
    });
  }, [reservationTimeslots]);

  return (
    <div className="text-[18px]">
      <div>
        <div className=" mt-4">
          {isSubmitting && (
            <div className="h-[40vh] flex flex-col justify-center items-center text-center text-gray-500">
              <div className="text-[#e31836]">
                <span className="material-symbols thin text-[100px] animate-spin">
                  progress_activity
                </span>
              </div>
            </div>
          )}
          {isSubmitted && (
            <div className="min-h-[40vh] flex flex-col justify-center items-center text-center text-gray-500">
              {formStatus != 200 ? (
                <div className="text-[#e31836]">
                  <span className="material-symbols thin text-[100px]">
                    error
                  </span>
                  <div className="text-2xl">{t('submitFail')}</div>
                  <div className="px-4 pt-4 max-w-[450px] m-auto text-md leading-6">
                    {errorMsg}
                  </div>

                  {['9901002', '9901003'].includes(sysCode) ? (
                    <button
                      className="bg-[#e31836] text-white text-lg py-2 px-4 rounded-md my-10 hover:shadow-lg w-[250px]"
                      onClick={() => {
                        window.location.reload();
                      }}
                    >
                      {t('reloadPage')}
                    </button>
                  ) : (
                    <button
                      className="bg-[#e31836] text-white text-lg py-2 px-4 rounded-md my-10 hover:shadow-lg w-[250px]"
                      onClick={() => {
                        setIsSubmitted(false);
                      }}
                    >
                      {t('retry')}
                    </button>
                  )}
                </div>
              ) : (
                <div className="text-[#e31836]">
                  <span className="material-symbols thin text-[100px]">
                    check_circle
                  </span>
                  <div className="text-2xl">{t('submitSuccess')}</div>
                  <button
                    className="bg-[#e31836] text-white text-lg py-2 px-4 rounded-md my-10 hover:shadow-lg w-[250px]"
                    onClick={() => {
                      setIsSubmitted(false);
                    }}
                  >
                    {t('submitAgain')}
                  </button>
                </div>
              )}
            </div>
          )}
          {!isSubmitted && !isSubmitting && (
            <form
              encType="multipart/form-data"
              name="form"
              onSubmit={submitForm}
              action={meta.url}
              method={meta.method}
            >
              <div className="grid grid-cols-2 md:grid-cols-4 w-full pb-10 gap-4 md:gap-6 py-2 px-5 md:py-[69px] md:px-[65px]">
                {meta?.fields.map((field: any, index: number) => {
                  const fieldRow = field[0];
                  const fieldBasic = fieldRow.split(':');
                  const fieldType = fieldBasic[0];
                  const fieldKey = fieldBasic[1];
                  const fieldTitle = fieldRow.substr(
                    fieldType.length + fieldKey.length + 2
                  );

                  const fieldSettings = field[1];
                  switch (fieldType) {
                    case 'title':
                      return (
                        <div
                          key={index}
                          className="w-full md:col-span-4 col-span-2"
                        >
                          {formSectionTitle(fieldKey, {
                            t_class: 'font-bold',
                            ...fieldSettings
                          })}
                        </div>
                      );

                    case 'select':
                      const optionGroups: string[] = [];

                      const reservation_slug = fieldSettings?.reservation_time;
                      if (!reservation_slug) {
                        if (typeof fieldSettings?.options == 'string') {
                          optionGroups.push(fieldSettings?.options);
                        } else {
                          fieldSettings?.options.map((option: string) => {
                            optionGroups.push(option);
                          });
                        }
                      }

                      return (
                        <div
                          key={index}
                          className={cn(
                            'flex flex-col',
                            reservation_slug ? '' : 'col-span-2 '
                          )}
                        >
                          {formTitle(fieldKey, fieldTitle, fieldSettings)}
                          <div className=" grid grid-cols-1 ">
                            <select
                              name={fieldKey}
                              id={fieldKey}
                              className={cn(
                                'col-start-1 row-start-1 w-full appearance-none rounded-sm bg-white py-1.5',
                                'pr-8 pl-4 text-gray-900 border border-gray-300 text-[20px]',
                                'w-full rounded-sm  col-span-2 border border-[#8a5c43] afacad ',
                                'h-[48px] text-[#575757] cursor-pointer hover:bg-[#f5f5f5]'
                              )}
                              value={formData[fieldKey]}
                              onChange={(e: any) => {
                                updateFormData(e, fieldKey);
                                setFieldErrorMap((prev: any) => ({
                                  ...prev,
                                  [fieldKey]: ''
                                }));
                                e?.target?.setCustomValidity('');
                              }}
                              required={fieldSettings?.required}
                              onInvalid={(e: any) => {
                                onInvalid(e, fieldTitle, fieldKey);
                              }}
                            >
                              {reservation_slug
                                ? availableTimeslots?.map((a: any) => {
                                    return (
                                      a.date ==
                                        selectedDates[reservation_slug] &&
                                      a.slug == reservation_slug && (
                                        <option
                                          className={cn(
                                            'bg-[#363533] text-[#FBF8F3] leading-[24px] '
                                          )}
                                          key={a.date + '-' + a.timeslot.value}
                                          value={a.timeslot.value}
                                        >
                                          {a.timeslot.label}
                                        </option>
                                      )
                                    );
                                  })
                                : optionGroups.map((optionGroup, index) => {
                                    const optionGroupLabel =
                                      optionGroup.indexOf('=') > 1
                                        ? optionGroup.split('=')[0]
                                        : null;
                                    if (optionGroupLabel) {
                                      return (
                                        <optgroup
                                          key={'optgroup_' + index}
                                          label={optionGroupLabel}
                                        >
                                          {formOption(
                                            optionGroup.substring(
                                              optionGroup.indexOf('=') + 1
                                            )
                                          )}
                                        </optgroup>
                                      );
                                    } else {
                                      return formOption(optionGroup);
                                    }
                                  })}
                            </select>
                            <div className="pointer-events-none col-start-1 row-start-1 mr-2 flex items-center justify-self-end  ">
                              <span className="material-symbols outlined thin-300 text-[#8a5c43]">
                                keyboard_arrow_down
                              </span>
                            </div>
                          </div>
                        </div>
                      );

                    case 'email':
                      return (
                        <div key={index} className="col-span-2 flex flex-col">
                          {formTitle(fieldKey, fieldTitle, fieldSettings)}
                          <input
                            name={fieldKey}
                            id={fieldKey}
                            type="email"
                            placeholder={fieldSettings?.placeholder}
                            className={cn(
                              'w-full rounded-sm px-2 col-span-2 border border-[#8a5c43] h-[48px]',
                              fieldSettings?.class
                            )}
                            value={formData[fieldKey] || ''}
                            onChange={(e) => updateFormData(e, fieldKey)}
                            onBlur={(e) => validateEmail(e, fieldKey)}
                            required={fieldSettings?.required}
                            onInput={(e: any) => {
                              onInput(e, fieldKey);
                            }}
                            onInvalid={(e: any) => {
                              onInvalid(e, fieldTitle, fieldKey);
                            }}
                          />
                          <div className="text-sm text-[#e31836]">
                            {fieldErrorMap[fieldKey]}
                          </div>
                        </div>
                      );
                    case 'file':
                      return (
                        <div key={index} className="mb-4">
                          {formTitle(fieldKey, fieldTitle, fieldSettings)}
                          <input
                            name={fieldKey}
                            id={fieldKey}
                            type="file"
                            className={cn(
                              'border border-gray-300 rounded-sm w-full text-sm p-1',
                              fieldSettings?.class
                            )}
                            onChange={(e) => {
                              const files = e.target.files;
                              setFormData((prev: any) => ({
                                ...prev,
                                [fieldKey]: files ? files[0] : null
                              }));
                            }}
                            required={fieldSettings?.required}
                            onInput={(e: any) => {
                              onInput(e, fieldKey);
                            }}
                            onInvalid={(e: any) => {
                              onInvalid(e, fieldTitle, fieldKey);
                            }}
                          />
                        </div>
                      );
                    case 'textarea':
                      return (
                        <div
                          key={index}
                          className="md:col-span-4 col-span-2 flex flex-col"
                        >
                          {formTitle(fieldKey, fieldTitle, fieldSettings)}
                          <textarea
                            name={fieldKey}
                            id={fieldKey}
                            className={cn(
                              'resize-y w-full rounded-sm p-2 col-span-2 border border-[#8a5c43] ',
                              fieldSettings?.class
                            )}
                            placeholder={fieldSettings?.placeholder}
                            value={formData[fieldKey] || ''}
                            onChange={(e) => updateFormData(e, fieldKey)}
                            required={fieldSettings?.required}
                            maxLength={fieldSettings?.maxlength ?? 5000}
                            onInput={(e: any) => {
                              onInput(e, fieldKey);
                            }}
                            onInvalid={(e: any) => {
                              onInvalid(e, fieldTitle, fieldKey);
                            }}
                          />
                        </div>
                      );
                    case 'date':
                      const reservation_d_slug =
                        fieldSettings?.reservation_date;
                      return (
                        <div
                          key={index}
                          className={cn(
                            'flex flex-col',
                            reservation_d_slug ? '' : 'col-span-2 '
                          )}
                        >
                          <input
                            type="hidden"
                            name={fieldKey}
                            value={(
                              formData[fieldKey] ?? new Date()
                            ).toLocaleDateString()}
                          />
                          {formTitle(fieldKey, fieldTitle, fieldSettings)}
                          <DatePicker
                            className={cn(
                              'border  rounded-sm w-full text-sm p-1',
                              fieldSettings?.class
                            )}
                            btnClassName={cn(
                              'h-[48px] border border-[#8a5c43] w-full text-[20px] text-[#575757]',
                              fieldSettings?.btnClass
                            )}
                            placeholder={fieldSettings?.placeholder}
                            value={formData[fieldKey] || ''}
                            onChange={(date: Date) => {
                              setFormData((prev: any) => ({
                                ...prev,
                                [fieldKey]: date
                              }));
                              setSelectedDates((prev: any) => {
                                const selectDate =
                                  moment(date).format(dateFormat);
                                meta?.fields.map((f: any) => {
                                  const fRow = f[0];
                                  const fBasic = fRow.split(':');
                                  const fKey = fBasic[1];
                                  const fSettings = f[1];
                                  const slug = fSettings?.reservation_time;
                                  if (
                                    slug === fieldSettings?.reservation_date
                                  ) {
                                    const timeslot = reservationTimeslots[slug];
                                    if (
                                      !timeslot?.[selectDate]?.find(
                                        (t: any) => t.value === formData[fKey]
                                      )
                                    ) {
                                      setFormData((prev: any) => ({
                                        ...prev,
                                        [fKey]:
                                          timeslot?.[selectDate]?.[0].value ??
                                          ''
                                      }));
                                    }
                                  }
                                });

                                return {
                                  ...prev,
                                  [fieldSettings?.reservation_date]: selectDate
                                };
                              });
                            }}
                            required={fieldSettings?.required}
                            maxLength={fieldSettings?.maxlength ?? 5000}
                            onInput={(e: any) => {
                              onInput(e, fieldKey);
                            }}
                            onInvalid={(e: any) => {
                              onInvalid(e, fieldTitle, fieldKey);
                            }}
                            {...(fieldSettings?.reservation_date
                              ? {
                                  disabled: (d: Date) =>
                                    checkReservation(
                                      d,
                                      fieldSettings?.reservation_date
                                    )
                                }
                              : {})}
                          />
                        </div>
                      );
                    case 'checkbox':
                      return (
                        <div
                          key={index}
                          className="md:col-span-4 col-span-2 flex flex-col"
                        >
                          {formCheckBox(fieldKey, fieldTitle, fieldSettings)}
                        </div>
                      );
                    case 'integer':
                      const min = fieldSettings?.min;
                      const max = fieldSettings?.max;
                      return (
                        <div key={index} className="col-span-2 flex flex-col">
                          {formTitle(fieldKey, fieldTitle, fieldSettings)}
                          <input
                            type="number"
                            name={fieldKey}
                            id={fieldKey}
                            className={cn(
                              'w-full rounded-sm px-2 col-span-2 border border-[#8a5c43] h-[48px]',
                              fieldSettings?.class
                            )}
                            placeholder={fieldSettings?.placeholder}
                            value={formData[fieldKey] || ''}
                            onChange={(e) => {
                              if (e.target.value == '') {
                                updateFormData(e, fieldKey);
                              }
                              // check if the input is a number
                              if (isNaN(parseInt(e.target.value))) {
                                return;
                              }
                              updateFormData(e, fieldKey);
                            }}
                            onBlur={(e) => {
                              const value = parseInt(e.target.value);

                              if (min && value < min) {
                                e.target.value = fieldSettings?.min;
                                updateFormData(e, fieldKey);
                              }
                              if (max && value > max) {
                                e.target.value = fieldSettings?.max;
                                updateFormData(e, fieldKey);
                              }
                            }}
                            onKeyDown={(e) => {
                              if (e.key.toLowerCase() == 'e' || e.key == '.') {
                                e.preventDefault();
                              }
                              if (fieldSettings?.min > 0) {
                                if (e.key == '-') {
                                  e.preventDefault();
                                }
                              }
                            }}
                            required={fieldSettings?.required}
                            maxLength={fieldSettings?.maxlength ?? 5000}
                            onInput={(e: any) => {
                              onInput(e, fieldKey);
                            }}
                            onInvalid={(e: any) => {
                              onInvalid(e, fieldTitle, fieldKey);
                            }}
                            {...(min > 0 ? {min} : {})}
                            {...(max > 0 ? {max} : {})}
                          />
                          <div className="text-sm text-[#e31836]">
                            {fieldErrorMap[fieldKey]}
                          </div>
                        </div>
                      );
                    default:
                      return (
                        <div key={index} className="col-span-2 flex flex-col">
                          {formTitle(fieldKey, fieldTitle, fieldSettings)}
                          <input
                            name={fieldKey}
                            id={fieldKey}
                            className={cn(
                              'w-full rounded-sm px-2 col-span-2 border border-[#8a5c43] h-[48px]',
                              fieldSettings?.class
                            )}
                            placeholder={fieldSettings?.placeholder}
                            value={formData[fieldKey] || ''}
                            onChange={(e) => updateFormData(e, fieldKey)}
                            required={fieldSettings?.required}
                            maxLength={fieldSettings?.maxlength ?? 5000}
                            onInput={(e: any) => {
                              onInput(e, fieldKey);
                            }}
                            onInvalid={(e: any) => {
                              onInvalid(e, fieldTitle, fieldKey);
                            }}
                          />
                          <div className="text-sm text-[#e31836]">
                            {fieldErrorMap[fieldKey]}
                          </div>
                        </div>
                      );
                  }
                })}
              </div>

              <div className="afacad bg-[#f7f6f1] ">
                <div className="flex flex-col m-auto bg-[#363533] text-[#f7f6f1] text-[20px] gap-[32px] max-w-[1050px] px-5 md:px-[64px] md:pb-[43px] pt-[43px] ">
                  {meta?.terms.map((field: any, index: number) => {
                    const fieldRow = field[0];
                    const fieldBasic = fieldRow.split(':');
                    const fieldType = fieldBasic[0];
                    const fieldKey = fieldBasic[1];
                    const fieldTitle = fieldRow.substr(
                      fieldType.length + fieldKey.length + 2
                    );

                    const fieldSettings = field[1];
                    switch (fieldType) {
                      case 'checkbox':
                        return (
                          <div
                            key={index}
                            className="md:col-span-4 col-span-2 flex flex-col"
                          >
                            {formCheckBox(fieldKey, fieldTitle, fieldSettings)}
                          </div>
                        );
                    }
                  })}
                  <div className="text-right">
                    <Human hash={hash}>
                      <button
                        type="submit"
                        className={cn(
                          'py-2 px-10 afacad rounded-full m-auto font-[500]',
                          'hover:bg-[#9C0001] hover:text-[#F7F6F1]',
                          'bg-[#FFFEE9] text-[#363533]  font-bold',
                          'hover:shadow-lg w-full md:w-fit mb-10 md:mb-0 h-[53px]',
                          locale == 'zh' ? 'text-[24px]' : 'text-[28px]'
                        )}
                      >
                        {t('submit')}
                      </button>
                    </Human>
                  </div>
                </div>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
